import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  FaFacebook, FaInstagram, FaTiktok, FaPinterest, FaTwitter,
  FaCcVisa, FaCcMastercard, FaCcPaypal, FaApplePay, FaGooglePay,
  FaArrowRight, FaMapMarkerAlt, FaEnvelope, FaPhone, FaWhatsapp,
  FaTimes,
} from 'react-icons/fa';
import logo from '../assets/logo.jpeg';
import { useWhatsAppSettings } from '../hooks/useWhatsAppSettings';
import { useSiteContent } from '../hooks/useSiteContent';
import { getCurrentProduct } from '../hooks/currentProductStore';
import './Footer.css';

const Footer = () => {
  const [email, setEmail] = useState('');
  const [subscribed, setSubscribed] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const location = useLocation();
  const { openWhatsApp, settings, hasNumber } = useWhatsAppSettings();
  const { content } = useSiteContent();
  const { footer, social } = content;

  // Don't show the floating button on admin pages
  const isAdmin = location.pathname.startsWith('/admin');

  // Build a context-aware message for the floating bubble.
  // If the visitor is on a product page, the conversation opens about that product.
  const getFloatingMessage = () => {
    const product = getCurrentProduct();
    if (product) {
      const price = product.discountPrice || product.price;
      return [
        `Hi! I'm looking at *${product.name}*${price ? ` (${price > 0 ? `KSh ${Number(price).toFixed(0)}` : ''})` : ''} on your website.`,
        product.description
          ? `\n\n${product.description.slice(0, 180)}${product.description.length > 180 ? '…' : ''}`
          : '',
        `\n\nCould you help me with more details or availability? 😊`,
      ].join('');
    }
    return settings.footerMessage;
  };

  const handleSubscribe = (e) => {
    e.preventDefault();
    if (email.trim()) {
      setSubscribed(true);
      setEmail('');
    }
  };

  const shopLinks = [
    { label: 'All Swimwear',  to: '/shop' },
    { label: 'New Arrivals',  to: '/shop?sort=newest' },
    { label: 'Collections',  to: '/categories' },
    { label: 'Brands',       to: '/brands' },
    { label: 'Summer Sale 🔥', to: '/shop?sort=discount' },
  ];

  const supportLinks = [
    { label: 'Size Guide',    to: '/size-guide' },
    { label: 'Shipping Info', to: '/shipping' },
    { label: 'Returns',       to: '/returns' },
    { label: 'FAQ',           to: '/faq' },
    { label: 'Contact Us',    to: '/contact' },
  ];

  const companyLinks = [
    { label: 'About Us',  to: '/about' },
    { label: 'Blog',      to: '/blog' },
    { label: 'Careers',   to: '/careers' },
    { label: 'Press',     to: '/press' },
  ];

  const socials = [
    { icon: <FaInstagram />, href: social.instagram, label: 'Instagram' },
    { icon: <FaTiktok />,    href: social.tiktok,    label: 'TikTok' },
    { icon: <FaFacebook />,  href: social.facebook,  label: 'Facebook' },
    { icon: <FaPinterest />, href: social.pinterest, label: 'Pinterest' },
    { icon: <FaTwitter />,   href: social.twitter,   label: 'Twitter' },
  ].filter(s => s.href);

  return (
    <>
    {/* ── Floating WhatsApp bubble ── */}
    {hasNumber && !isAdmin && (
      <div className="wa-float-wrap">
        {showTooltip && (() => {
          const onProduct = Boolean(getCurrentProduct());
          return (
            <div className="wa-float-tooltip">
              <button className="wa-float-tooltip-close" onClick={() => setShowTooltip(false)} aria-label="Close">
                <FaTimes />
              </button>
              <p>
                {onProduct
                  ? `👋 Have a question about this product?`
                  : `👋 Hi! Chat with us on WhatsApp`}
              </p>
              {onProduct && getCurrentProduct() && (
                <p className="wa-float-product-name">
                  {getCurrentProduct().name}
                </p>
              )}
              <button
                className="wa-float-tooltip-btn"
                onClick={() => { openWhatsApp(getFloatingMessage()); setShowTooltip(false); }}
              >
                <FaWhatsapp /> {onProduct ? 'Ask about this product' : 'Start Chat'}
              </button>
            </div>
          );
        })()}
        <button
          className="wa-float-btn"
          onClick={() => setShowTooltip((v) => !v)}
          aria-label="Chat on WhatsApp"
          title="Chat with us on WhatsApp"
        >
          {showTooltip ? <FaTimes /> : <FaWhatsapp />}
        </button>
      </div>
    )}

    <footer className="footer">
      {/* Wave top decoration */}
      <div className="footer-wave">
        <svg viewBox="0 0 1440 80" preserveAspectRatio="none">
          <path d="M0,40 C360,80 1080,0 1440,40 L1440,80 L0,80 Z" fill="#1a1a2e" />
        </svg>
      </div>

      <div className="footer-body">
        <div className="footer-container">

          {/* ── Row 1: Brand + Columns + Newsletter ── */}
          <div className="footer-grid">

            {/* Brand */}
            <div className="footer-brand-col">
              <Link to="/" className="footer-logo-link">
                <img src={logo} alt="Bikini World" className="footer-logo-img" />
              </Link>
              <p className="footer-tagline">
                {footer.tagline}
              </p>

              <div className="footer-contact">
                {footer.email && (
                  <a href={`mailto:${footer.email}`} className="footer-contact-item">
                    <FaEnvelope /> {footer.email}
                  </a>
                )}
                {footer.phone && (
                  <a href={`tel:${footer.phone.replace(/\s+/g, '')}`} className="footer-contact-item">
                    <FaPhone /> {footer.phone}
                  </a>
                )}
                {footer.location && (
                  <span className="footer-contact-item">
                    <FaMapMarkerAlt /> {footer.location}
                  </span>
                )}
              </div>

              <div className="footer-socials">
                {socials.map((s) => (
                  <a key={s.label} href={s.href} className="footer-social-btn" aria-label={s.label} target="_blank" rel="noreferrer">
                    {s.icon}
                  </a>
                ))}
              </div>
            </div>

            {/* Shop links */}
            <div className="footer-links-col">
              <h5 className="footer-col-title">Shop</h5>
              <ul>
                {shopLinks.map((l) => (
                  <li key={l.to}>
                    <Link to={l.to} className="footer-link">{l.label}</Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Support links */}
            <div className="footer-links-col">
              <h5 className="footer-col-title">Support</h5>
              <ul>
                {supportLinks.map((l) => (
                  <li key={l.to}>
                    <Link to={l.to} className="footer-link">{l.label}</Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Company links */}
            <div className="footer-links-col">
              <h5 className="footer-col-title">Company</h5>
              <ul>
                {companyLinks.map((l) => (
                  <li key={l.to}>
                    <Link to={l.to} className="footer-link">{l.label}</Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Newsletter */}
            <div className="footer-newsletter-col">
              <h5 className="footer-col-title">Beach Updates</h5>
              <p className="footer-nl-text">
                Get exclusive deals, new arrivals & styling tips straight to your inbox.
              </p>
              {subscribed ? (
                <div className="footer-nl-success">
                  🎉 You're in! Check your inbox.
                </div>
              ) : (
                <form className="footer-nl-form" onSubmit={handleSubscribe}>
                  <input
                    type="email"
                    placeholder="Your email address"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                  <button type="submit" aria-label="Subscribe">
                    <FaArrowRight />
                  </button>
                </form>
              )}

              {/* Payment methods */}
              <div className="footer-payment">
                <p className="footer-payment-label">Secure Payments</p>
                <div className="footer-payment-icons">
                  <FaCcVisa />
                  <FaCcMastercard />
                  <FaCcPaypal />
                  <FaApplePay />
                  <FaGooglePay />
                </div>
              </div>
            </div>
          </div>

          {/* ── Divider ── */}
          <div className="footer-divider" />

          {/* ── Bottom bar ── */}
          <div className="footer-bottom">
            <p className="footer-copy">
              © {new Date().getFullYear()} BikiniWorld Kenya. All rights reserved. &nbsp;🏖️
            </p>
            <div className="footer-legal">
              <Link to="/privacy">Privacy Policy</Link>
              <span>·</span>
              <Link to="/terms">Terms of Service</Link>
              <span>·</span>
              <Link to="/cookies">Cookies</Link>
            </div>
          </div>

        </div>
      </div>
    </footer>
    </>
  );
};

export default Footer;
